import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, PencilLine, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { Button } from '@ui/button.tsx';
import type {
  TypologyTypologyReadModel,
  TypologyTypologyWriteModel
} from '@r/api/generated/client.schemas.ts';

type Typology = TypologyTypologyReadModel | TypologyTypologyWriteModel;

export const useTypologiesColumns = (
  createdAt: boolean,
  onEdit?: (typology: TypologyTypologyReadModel) => void,
  onDelete?: (typology: TypologyTypologyReadModel) => void
): ColumnDef<Typology>[] => {
  const { t } = useTranslation();

  const columns: ColumnDef<Typology>[] = [
    {
      accessorKey: 'name',
      header: t('admin.typology.column.name'),
      cell: ({ row }) => {
        return row.original.isDefault
          ? `${row.original.name} (${t('admin.typology.default')})`
          : row.original.name;
      }
    },
    {
      accessorKey: 'pointCoeff',
      header: t('admin.typology.column.coefficient'),
      cell: ({ row }) => {
        const amountForOnePoint = (1 / row.original.pointCoeff).toFixed(2);
        return `${row.original.pointCoeff} (1 ${t('point.name_one')} = ${amountForOnePoint} €)`;
      }
    }
  ];

  if (createdAt) {
    columns.push({
      accessorKey: 'createdAt',
      header: t('admin.typology.column.created.at'),
      cell: ({ row }) => {
        const typology = row.original as TypologyTypologyReadModel;
        return typology.createdAt
          ? new Date(typology.createdAt as string).toLocaleDateString()
          : '';
      }
    });
  }

  if (onEdit || onDelete) {
    columns.push({
      id: 'actions',
      cell: ({ row }) => {
        const typology = row.original as TypologyTypologyReadModel;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex h-8 w-8 p-0 data-[state=open]:bg-muted float-end"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(typology)}>
                  <PencilLine className="mr-2 w-4 h-4" />
                  {t('admin.typology.action.edit')}
                </DropdownMenuItem>
              )}

              {onEdit && onDelete && <DropdownMenuSeparator />}

              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(typology)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 w-4 h-4" />
                  {t('admin.typology.action.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    });
  }

  return columns;
};
